// src/pages/trading-portal/AIAnalysisEngine/AIAnalysisEngineDashboard.js
// AI Analysis Engine dashboard overview

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    CircularProgress,
    Alert,
    Button,
    Chip,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TimelineIcon from '@mui/icons-material/Timeline';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    VolModelTypeLabels,
    VolModelTypeColors,
    VolForecastHorizonLabels,
    VolForecastStatusLabels,
    VolForecastStatusColors,
    MarketRegimeTypeLabels,
    MarketRegimeTypeColors,
    SignalDirectionLabels,
    SignalDirectionColors,
    SignalStrengthLabels,
    SignalStrengthColors,
    AAESignalStatusLabels,
    AAESignalStatusColors,
    SentimentScoreLabels,
    SentimentScoreColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const NAV_ITEMS = [
    { label: 'Volatility Prediction', icon: ShowChartIcon, path: '/trading-portal/ai-analysis/volatility-prediction', color: ACCENT, description: 'GARCH, HAR-RV, and ensemble volatility forecasting' },
    { label: 'Market Regime Detection', icon: BubbleChartIcon, path: '/trading-portal/ai-analysis/market-regime', color: '#3b82f6', description: 'HMM regime detection, vol regimes, and tail risk' },
    { label: 'Signal Generation', icon: SignalCellularAltIcon, path: '/trading-portal/ai-analysis/signal-generation', color: '#10b981', description: 'Multi-factor signal generation and conflict resolution' },
    { label: 'Sentiment & Alt Data', icon: NewspaperIcon, path: '/trading-portal/ai-analysis/sentiment-data', color: '#8b5cf6', description: 'News sentiment, social media, options flow, and macro' },
];

// Sub-components
const StatCard = ({ icon: Icon, title, value, color, subtitle, onClick }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            cursor: onClick ? 'pointer' : 'default',
            '&:hover': onClick
                ? { borderColor: color, boxShadow: `0 4px 20px ${color}20`, transform: 'translateY(-2px)' }
                : {},
        }}
        onClick={onClick}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '14px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ fontFamily, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}
                    >
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="caption"
                            sx={{ fontFamily, color: '#94a3b8', fontWeight: 500 }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const StatCardSkeleton = () => (
    <Card sx={{ height: '100%', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: '14px' }} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="60%" sx={{ fontSize: '0.8rem' }} />
                    <Skeleton variant="text" width="40%" sx={{ fontSize: '1.5rem' }} />
                </Box>
            </Box>
        </CardContent>
    </Card>
);

export default function AIAnalysisEngineDashboard() {
    const navigate = useNavigate();

    const [canView, setCanView] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Dashboard stats
    const [totalForecasts, setTotalForecasts] = useState(0);
    const [activeSignals, setActiveSignals] = useState(0);
    const [currentRegime, setCurrentRegime] = useState('--');
    const [currentRegimeType, setCurrentRegimeType] = useState('');
    const [latestSentiment, setLatestSentiment] = useState('--');
    const [latestSentimentType, setLatestSentimentType] = useState('');

    // Recent data
    const [recentForecasts, setRecentForecasts] = useState([]);
    const [activeSignalsList, setActiveSignalsList] = useState([]);

    useEffect(() => {
        const checkPerms = async () => {
            const view = await hasPermission('tr-aae-dashboard', 'view');
            setCanView(view);
        };
        checkPerms();
    }, []);

    const fetchDashboard = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        setError('');

        try {
            const [volRes, signalRes, regimeRes, sentimentRes] = await Promise.allSettled([
                axiosInstance.get('/api/volatility-prediction/dashboard'),
                axiosInstance.get('/api/signal-generation/dashboard'),
                axiosInstance.get('/api/market-regime-detection/dashboard'),
                axiosInstance.get('/api/sentiment-data/dashboard'),
            ]);

            // Volatility data
            if (volRes.status === 'fulfilled') {
                const volData = volRes.value.data?.data || volRes.value.data || {};
                setTotalForecasts(volData.total_forecasts_30d || volData.total_forecasts || 0);
                setRecentForecasts(volData.recent_forecasts || []);
            }

            // Signal data
            if (signalRes.status === 'fulfilled') {
                const sigData = signalRes.value.data?.data || signalRes.value.data || {};
                setActiveSignals(sigData.active_signals_count || sigData.active_signals || 0);
                setActiveSignalsList(sigData.active_signals_list || sigData.signals || []);
            }

            // Regime data
            if (regimeRes.status === 'fulfilled') {
                const regData = regimeRes.value.data?.data || regimeRes.value.data || {};
                const regime = regData.current_regime || regData.regime || '';
                setCurrentRegime(MarketRegimeTypeLabels[regime] || regime || '--');
                setCurrentRegimeType(regime);
            }

            // Sentiment data
            if (sentimentRes.status === 'fulfilled') {
                const senData = sentimentRes.value.data?.data || sentimentRes.value.data || {};
                const score = senData.latest_sentiment || senData.sentiment_score || '';
                setLatestSentiment(SentimentScoreLabels[score] || score || '--');
                setLatestSentimentType(score);
            }
        } catch (err) {
            console.error('Failed to fetch AI Analysis dashboard:', err);
            setError(err.response?.data?.message || 'Failed to load dashboard data.');
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (canView) {
            fetchDashboard();
        }
    }, [fetchDashboard, canView]);

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view the AI Analysis Engine Dashboard.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Welcome Header with Gradient */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PsychologyIcon sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                    AI Analysis Engine
                                </Typography>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                    Volatility forecasting, regime detection, signal generation, and sentiment analysis
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    {/* Current Regime Badge */}
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '14px', px: 2.5, py: 1.5, textAlign: 'center', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                            <TimelineIcon sx={{ fontSize: 18, color: '#fff' }} />
                            <Typography sx={{ fontFamily, fontWeight: 800, color: '#fff', fontSize: '1.1rem', lineHeight: 1 }}>
                                {loading ? '--' : currentRegime}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 500, mt: 0.25 }}>
                            Current Regime
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }} action={
                        <Button color="inherit" size="small" onClick={fetchDashboard} sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                            Retry
                        </Button>
                    }>
                        {error}
                    </Alert>
                )}

                {/* Stats Grid */}
                <Grid container spacing={2.5} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={ShowChartIcon}
                                title="Total Forecasts (30d)"
                                value={totalForecasts}
                                color={ACCENT}
                                subtitle="Volatility predictions"
                                onClick={() => navigate('/trading-portal/ai-analysis/volatility-prediction')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={SignalCellularAltIcon}
                                title="Active Signals"
                                value={activeSignals}
                                color="#10b981"
                                subtitle="Currently active"
                                onClick={() => navigate('/trading-portal/ai-analysis/signal-generation')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={BubbleChartIcon}
                                title="Current Regime"
                                value={currentRegime}
                                color={currentRegimeType === 'CRISIS' ? '#ef4444' : '#3b82f6'}
                                subtitle="Market state"
                                onClick={() => navigate('/trading-portal/ai-analysis/market-regime')}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        {loading ? <StatCardSkeleton /> : (
                            <StatCard
                                icon={SentimentSatisfiedIcon}
                                title="Latest Sentiment"
                                value={latestSentiment}
                                color="#8b5cf6"
                                subtitle="Aggregate score"
                                onClick={() => navigate('/trading-portal/ai-analysis/sentiment-data')}
                            />
                        )}
                    </Grid>
                </Grid>

                {/* Sub-Modules Navigation */}
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1.1rem', mb: 2 }}>
                        Sub-Modules
                    </Typography>
                    <Grid container spacing={2}>
                        {NAV_ITEMS.map((item) => (
                            <Grid item xs={12} sm={6} md={3} key={item.path}>
                                <Card
                                    sx={{
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: 'none',
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: item.color,
                                            boxShadow: `0 4px 20px ${item.color}20`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                    onClick={() => navigate(item.path)}
                                >
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: '14px',
                                                        bgcolor: `${item.color}15`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <item.icon sx={{ color: item.color, fontSize: 24 }} />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                                                        {item.label}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.78rem', fontWeight: 500 }}>
                                                        {item.description}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <NavigateNextIcon sx={{ color: '#cbd5e1', fontSize: 22 }} />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Bottom Grid: Recent Forecasts + Active Signals */}
                <Grid container spacing={3}>
                    {/* Recent Forecasts */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ShowChartIcon sx={{ color: ACCENT, fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Recent Forecasts
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/trading-portal/ai-analysis/volatility-prediction')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: ACCENT, fontSize: '0.78rem', '&:hover': { bgcolor: `${ACCENT}08` } }}
                                    >
                                        View All
                                    </Button>
                                </Box>

                                <Box sx={{ px: 1, pb: 2 }}>
                                    {loading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 1.5 }}>
                                                <Skeleton variant="circular" width={42} height={42} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem' }} />
                                                    <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
                                                </Box>
                                            </Box>
                                        ))
                                    ) : recentForecasts.length === 0 ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <ShowChartIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>
                                                No recent forecasts
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Run a volatility forecast to get started
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Symbol</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Model</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Horizon</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Forecast</TableCell>
                                                        <TableCell sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Status</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {recentForecasts.slice(0, 5).map((fc, index) => (
                                                        <TableRow key={fc.id || index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                                {fc.symbol || fc.symbol_name || '--'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={VolModelTypeLabels[fc.model_type] || fc.model_type}
                                                                    size="small"
                                                                    color={VolModelTypeColors[fc.model_type] || 'default'}
                                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                                                {VolForecastHorizonLabels[fc.horizon] || fc.horizon || '--'}
                                                            </TableCell>
                                                            <TableCell sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.82rem' }}>
                                                                {fc.forecast_value != null ? `${(fc.forecast_value * 100).toFixed(2)}%` : '--'}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={VolForecastStatusLabels[fc.status] || fc.status}
                                                                    size="small"
                                                                    color={VolForecastStatusColors[fc.status] || 'default'}
                                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Active Signals */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SignalCellularAltIcon sx={{ color: '#10b981', fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Active Signals
                                        </Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/trading-portal/ai-analysis/signal-generation')}
                                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', color: ACCENT, fontSize: '0.78rem', '&:hover': { bgcolor: `${ACCENT}08` } }}
                                    >
                                        View All
                                    </Button>
                                </Box>

                                <Box sx={{ px: 2, pb: 2 }}>
                                    {loading ? (
                                        Array.from({ length: 3 }).map((_, i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 0.5 }}>
                                                <Skeleton variant="circular" width={42} height={42} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem' }} />
                                                    <Skeleton variant="text" width="40%" sx={{ fontSize: '0.7rem' }} />
                                                </Box>
                                            </Box>
                                        ))
                                    ) : activeSignalsList.length === 0 ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <SignalCellularAltIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>
                                                No active signals
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Generate signals to see them here
                                            </Typography>
                                        </Box>
                                    ) : (
                                        activeSignalsList.slice(0, 5).map((signal, index) => (
                                            <Box
                                                key={signal.id || index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    py: 1.5,
                                                    px: 0.5,
                                                    borderBottom: '1px solid #f1f5f9',
                                                    '&:last-child': { borderBottom: 'none' },
                                                }}
                                            >
                                                <Box sx={{
                                                    width: 42, height: 42, borderRadius: '10px',
                                                    bgcolor: signal.direction === 'LONG_VOL' ? '#dcfce7' : signal.direction === 'SHORT_VOL' ? '#fee2e2' : '#f1f5f9',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    <TrendingUpIcon sx={{ fontSize: 22, color: signal.direction === 'LONG_VOL' ? '#10b981' : signal.direction === 'SHORT_VOL' ? '#ef4444' : '#94a3b8' }} />
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>
                                                        {signal.symbol || signal.symbol_name || 'Unknown'}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                                                        <Chip
                                                            label={SignalDirectionLabels[signal.direction] || signal.direction}
                                                            size="small"
                                                            color={SignalDirectionColors[signal.direction] || 'default'}
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.65rem', height: 20 }}
                                                        />
                                                        <Chip
                                                            label={SignalStrengthLabels[signal.strength] || signal.strength}
                                                            size="small"
                                                            color={SignalStrengthColors[signal.strength] || 'default'}
                                                            variant="outlined"
                                                            sx={{ fontFamily, fontWeight: 600, fontSize: '0.65rem', height: 20 }}
                                                        />
                                                    </Box>
                                                </Box>
                                                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                                                        {signal.confidence != null ? `${(signal.confidence * 100).toFixed(0)}%` : '--'}
                                                    </Typography>
                                                    {signal.created_at && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                                                            <AccessTimeIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                                                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                                                                {new Date(signal.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}
